import { useState, useMemo, useEffect } from "react";
import { CheckPicker } from "rsuite";

import useGlobalLists from "../../../api/api-hooks/cloud/useGlobalLists";
import { Module } from "../../../types/TenantInterfaces";
import ManageItems from "../../ManageItems/ManageItems";

interface Props {
  section: any;
  updateSection: any;
  module: Module | null | undefined;
}

const SortStages = ({ section, updateSection, module }: Props) => {
  const savedStages = section?.Query?.Fields;
  const stageField = section?.Query?.GroupBy?.Field;
  const [stages, setStages] = useState<{ id: string; title: string }[]>([]);
  const { globalLists, globalListsLoading } = useGlobalLists(!!stageField);

  const stagesList = useMemo(() => {
    if (!module || !stageField) return [];

    let sourceList: any = module.Fields.find(
      (f) => f.APIName === stageField
    )?.SourcesList;

    if (!sourceList) return [];

    if (typeof sourceList === "string" && sourceList.includes("Global.Lists")) {
      const listName = sourceList.replace("Global.Lists.", "");

      sourceList = globalLists?.find((l) => l.APIName === listName)?.Variables;
    }

    sourceList = sourceList.map((f: any, i: number) => ({
      id: `stage-${i}`,
      title: f.Name,
      label: f.Name,
      value: f.Value,
    }));

    sourceList.push({
      id: `stage-Undefined`,
      title: `No ${stageField}`,
      label: `No ${stageField}`,
      value: `No ${stageField}`,
    });
    return sourceList;
  }, [module, section, globalListsLoading]);

  useEffect(() => {
    if (!stagesList.length || !savedStages?.length) return;

    setStages(savedStages.map((s: string) => ({ id: `stage-${s}`, title: s })));
  }, [stagesList, savedStages]);

  return (
    <div>
      <CheckPicker
        onChange={(values: string[]) => {
          values = values.filter((v) => v !== "*");
          let newQuery = section?.Query;

          if (!values.length) {
            newQuery.Fields = ["*"];
            updateSection("Query", newQuery);
            return setStages([]);
          }

          newQuery.Fields = [...values];
          updateSection("Query", newQuery);
          setStages(values.map((v) => ({ id: `stage-${v}`, title: v })));
        }}
        value={savedStages}
        block
        className="my-2"
        data={stagesList}
        placement="auto"
        placeholder="Select Specific Stages"
      />
      <ManageItems
        items={stages}
        itemKey={undefined}
        idKey="id"
        nameKey="title"
        onReorder={reorderStages}
      />
    </div>
  );

  function reorderStages({ newOrder }: { newOrder: string[] }) {
    let fields: string[] = [];
    let newItems: { id: string; title: string }[] = [];

    for (const stageID of newOrder) {
      const item = stages.find((item) => item.id === stageID);

      if (!item) continue;

      fields.push(item.title);
      newItems.push(item);
    }

    updateSection("Query", { ...section.Query, Fields: fields });
    setStages(newItems);
  }
};

export default SortStages;
